import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { DueItem, DueItemList, DueItemsAnfrage, Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, Icon, useSofortPopup, useTelefon } from "@kundeninfo/ui";
import { Hinweis, Knopf, Optionen, Werkzeuggruppe, Werkzeugleiste } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { MilestonePanel } from "../milestones/MilestonePanel.js";
import type { VerlassenWaechter } from "../../router.js";
import { msBisMitternachtBerlin } from "../tickets/TicketBoard.js";
import { TicketAnlegen } from "../tickets/TicketAnlegen.js";
import { TicketDialog } from "../tickets/TicketDialog.js";
import type { TicketDialogProps } from "../tickets/TicketDialog.js";
import { verknuepfungenReiter } from "../tickets/TicketVerknuepfungen.js";
import { oeffneOderSpringe, useObjektPanel } from "../../components/objektPanel/ObjektPanelKontext.js";
import { berlinerTag } from "../tickets/ticketFormat.js";
import { SYMBOL_PLUS } from "../tickets/ticketSymbole.js";
import { chipSources, compareDueItems, requestSources, requestedItems } from "../termine/dueItems.js";
import type { DueSource } from "../termine/dueItems.js";
import { terminBezug } from "../termine/dueItems.js";
import { DueSourceChips } from "../termine/DueSourceChips.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { readDueSources, rememberDueSources } from "../termine/dueSourceMemory.js";
import type { CalendarClient } from "./calendarClient.js";
import { readCalendarTickets, readCalendarView, rememberCalendarTickets, rememberCalendarView } from "./calendarMemory.js";
import {
  CALENDAR_VIEWS, entriesByDay, entryFromDueItem, periodText, shiftAnchor, visibleEntries, windowOf,
} from "./calendarModel.js";
import type { CalendarEntry, CalendarView } from "./calendarModel.js";
import { CALENDAR_TEXT } from "./calendarTexts.js";
import { CalendarLegend, CalendarViews } from "./CalendarViews.js";
import { useCalendarKeys } from "./useCalendarKeys.js";
import "./Kalender.css";

/**
 * DER BEREICH „KALENDER“ (Spec D22, D23, D26; Plan T17, T20, T22) — die Termine aller zugeschalteten Quellen in den Sichten
 * Tag, Woche, Monat und Jahr. Navigation, Route und Kopfleiste hängt Aufgabe 10 ein; hier stehen Bereich und Modell.
 *
 * - Adresse (T22): `route.sicht` fehlt → die gemerkte Sicht (`ki-calendar-view`; Vorgabe Monat, Telefon Tag), `route.datum`
 *   fehlt → heute. Blättern, „Heute“ und Tastatur ersetzen den Verlaufseintrag (`onNavigiere(r, true)`), Sichtwechsel,
 *   „mehr“, Tage und Monate des Jahres legen einen an; nach diesen Sprüngen geht der Fokus an die neue Sicht. Ein Ticket
 *   öffnet sich über die Adresse (`ticketId`).
 * - Werkzeugleiste „Werkzeuge des Kalenders“: Unternehmen (Alle und je Kürzel, nicht gemerkt), „Meine | Alle“ (gemerkt,
 *   `ki-calendar-tickets`, Vorgabe Meine = `tickets=mine`), Hauptaktion „+ Meilenstein“. Darunter die Quellen-Chips mit
 *   „Tickets“ (Wahl gemeinsam mit Liste und Zeitachse, D22) und die Gruppe „Zeitraum“: Sicht, ◂ „Heute“ ▸, Zeitraumtext
 *   fester Breite mit `aria-live`; dahinter die Legende der Zustandszeichen (G1, nicht am Telefon).
 * - Laden: `listDueItems` mit dem Fenster der Sicht, den Quellen der Chips, Unternehmen, `ticketsMine` und — nur in der
 *   Sicht Tag auf heute — `overdue` (vor `from` liefert der Server nur OVERDUE, Ruling G1). Ohne zugeschaltete Quelle keine
 *   Abfrage (der Klient machte aus `sources: []` „alle Quellen“, Review Aufgabe 2, H3). Neu beim Öffnen, bei Fensterfokus,
 *   alle 60 s (nicht im verborgenen Tab; beim Sichtbarwerden einmal, G3), nach Mitternacht und nach jeder Änderung im
 *   Dialog oder Panel; Takt und Fokus nicht, solange im Dialog oder Panel Ungespeichertes steht. Nur die jüngste Antwort
 *   zeichnet; ein Fehler lässt den Stand stehen. Gezeichnet werden nur die angefragten Quellen (G8). Ein Rollfeld
 *   `.kal__rollfeld` über alle Zustände und Sichten (Scrollposition bleibt beim Neuladen); `aria-busy` heißt „Laden läuft“.
 * - D26 mit H2: Bei „Tickets an“ tritt ein Fremdeintrag hinter sein Fälligkeitsticket am selben Tag zurück, wenn das Ticket
 *   in derselben Antwort steht.
 * - „Ticket anlegen“ (Aufgabe 12): Hat die Fälligkeit schon ein Ticket (409), öffnet sich dieses mit einem Hinweis (H10).
 * - Sprünge (T20): Ticket → Ticketdialog rechts (Adresse mit `ticketId`), Meilenstein → Panel (`MilestonePanel`, D21),
 *   Lieferantenobjekt → Objektpanel, Beleg → Akte (`oeffneOderSpringe`, Quelle „calendar“). Nach dem Schließen mit Änderung lädt der Kalender neu; der Fokus geht
 *   an den Eintrag zurück. Wie im Bereich Tickets fragt ein Verlassen-Wächter gestapelt nach, solange im Dialog oder Panel
 *   Ungespeichertes steht. Eltern- und Kind-Ticket des Dialogs öffnen als Objektpanel seitlich daneben (Gastgeber, F14;
 *   der Dialog der Adresse ist dort `darunter`); hat das Panel geschrieben, laden Dialog und Kalender nach.
 * - Meilensteine (Aufgabe 8): Hauptaktion „+ Meilenstein“ mit festem Platz (Leiste kippt nie), abgeschaltet, solange die
 *   Quelle „Meilensteine“ aus ist — mit dem Unternehmen der Leiste, bei „Alle“ dem Arbeitsunternehmen. Der Reiter „Verknüpfungen“ des
 *   Ticketdialogs trägt den Abschnitt „Meilensteine“ wie im Bereich Tickets; sein Panel liegt seitlich über dem Dialog, und
 *   hat es geschrieben, lädt der Dialog nach (`reloadToken`).
 */

export type KalenderRoute = { sicht: CalendarView | null; datum: string | null; ticketId: string | null };

export type BereichKalenderProps = {
  client: CalendarClient; companies: readonly Unternehmen[];
  ich: { id: string; name: string; istAdmin: boolean; supplierView: boolean };
  arbeitsunternehmenId: string | null;
  route: KalenderRoute; onNavigiere: (r: KalenderRoute, ersetzen: boolean) => void;
  zusatzReiter?: TicketDialogProps["zusatzReiter"]; registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

const TICK_MS = 60_000;
const ALL_COMPANIES = "ALL";
const T = CALENDAR_TEXT;

export function BereichKalender(p: BereichKalenderProps): ReactElement {
  const phone = useTelefon();
  const userId = p.ich.id;
  // Der Berliner Tag der Uhr (F13, wie die Liste); der Takt nach Mitternacht zeichnet neu und lädt das neue „heute“.
  const [, setMidnight] = useState(0);
  const heute = berlinerTag(new Date().toISOString());
  const view = p.route.sicht ?? readCalendarView(userId, phone);
  const anchor = p.route.datum ?? heute;
  const { from, to } = windowOf(view, anchor);
  const overdue = view === "DAY" && anchor === heute;

  const [company, setCompany] = useState(ALL_COMPANIES);
  const [tickets, setTickets] = useState(() => readCalendarTickets(userId));
  const [sources, setSources] = useState<DueSource[]>(() => readDueSources(userId));
  const requested = requestSources(sources, "CALENDAR", p.ich.supplierView);
  // Die Anfrage hängt am Schlüssel, nicht am Array: ein neu gebautes, gleiches Array lädt nicht neu.
  const requestKey = requested.join(",");
  const ticketsOn = requested.includes("TICKET");
  const milestonesOn = requested.includes("MILESTONE");

  // ── Dialog und Panel (wie BereichTickets: Verlassen-Wächter, Fokus zurück) ─────────────────────────────────────
  const [milestone, setMilestone] = useState<{ id: string | null; companyId: string | null; aside: boolean } | null>(null);
  /** Hat das Panel über dem Ticketdialog geschrieben, lädt der Dialog nach (wie der Kind-Dialog im Bereich Tickets). */
  const [dialogReload, setDialogReload] = useState(0);
  const [dialogDirty, setDialogDirty] = useState<{ id: string; dirty: boolean }>({ id: "", dirty: false });
  const [panelDirty, setPanelDirty] = useState(false);
  /** „Ticket anlegen“ am Eintrag (Aufgabe 12): der Termin, aus dem „Aufgabe anlegen“ offen ist, und ob darin Eingaben stehen. */
  const [creating, setCreating] = useState<DueItem | null>(null);
  const [creatingDirty, setCreatingDirty] = useState(false);
  /** H10: Das Ticket, das nach 409 statt eines neuen aufging — sein Dialog sagt es, bis die Adresse ein anderes oder keines
   *  nennt. */
  const [takenTicket, setTakenTicket] = useState<string | null>(null);
  const dialogUnsaved = p.route.ticketId !== null && dialogDirty.id === p.route.ticketId && dialogDirty.dirty;
  const unsaved = dialogUnsaved || (milestone !== null && panelDirty) || (creating !== null && creatingDirty);
  const released = useRef(false);
  const register = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!unsaved || register === undefined) return undefined;
    released.current = false;
    return register(() => (released.current ? true : darfAbbrechen(true)));
  }, [unsaved, register]);
  /** Ein Weg, den Dialog oder Panel selbst beschlossen haben: ohne zweite Frage. */
  const release = () => {
    released.current = true;
    setDialogDirty({ id: "", dirty: false });
    setPanelDirty(false);
    setCreatingDirty(false);
  };
  const dialogReports = useCallback((dirty: boolean) => {
    if (p.route.ticketId !== null) setDialogDirty({ id: p.route.ticketId, dirty });
  }, [p.route.ticketId]);
  // H3: Wechselt die Adresse — nach der Freigabe des Wächters etwa über die Zurück-Taste —, schließen Meilenstein-Panel und
  // „Aufgabe anlegen“ mit: sie gehören zum alten Stand, und „Verwerfen“ hat ihre Eingaben aufgegeben (Muster „Zustand bei
  // geänderter Eingabe zurücksetzen“). Eigene Wege (Ticket öffnen, Anlegen) schließen sie schon vorher.
  // Der Hinweis nach 409 (H10) gilt nur dem Ticket, das daraufhin aufging.
  const routeKey = `${p.route.sicht ?? ""}|${p.route.datum ?? ""}|${p.route.ticketId ?? ""}`;
  const [routeSeen, setRouteSeen] = useState(routeKey);
  if (routeSeen !== routeKey) {
    setRouteSeen(routeKey);
    setMilestone(null);
    setPanelDirty(false);
    setCreating(null);
    setCreatingDirty(false);
    if (p.route.ticketId !== takenTicket) setTakenTicket(null);
  }

  // ── Laden ──────────────────────────────────────────────────────────────────────────────────────────────────
  const [data, setData] = useState<DueItemList | null>(null);
  /** Das Fenster (`from|to`), dessen letzte Abfrage scheiterte; `null` nach einer Antwort. */
  const [failed, setFailed] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);
  const run = useRef(0);
  const load = useCallback(async () => {
    const current = ++run.current;
    if (requestKey === "") {
      // H3: ohne Quelle keine Abfrage — eine leere Antwort am Ort; die Feiertage des letzten Stands bleiben für die Tönung.
      setData((old) => ({ heute, from, to, items: [], total: 0, truncated: false, kalender: old?.kalender ?? [] }));
      setFailed(null);
      return;
    }
    const query: DueItemsAnfrage = { from, to, sources: requestKey.split(","),
      ...(company === ALL_COMPANIES ? {} : { company }), ...(tickets === "MINE" ? { ticketsMine: true } : {}),
      ...(overdue ? { overdue: true } : {}) };
    try {
      const r = await p.client.listDueItems(query);
      if (current !== run.current) return;
      setData(r);
      setFailed(null);
    } catch {
      if (current === run.current) setFailed(`${from}|${to}`);
    }
  }, [p.client, heute, from, to, requestKey, company, tickets, overdue]);
  useEffect(() => { void load(); }, [load, reloadCount]);
  const loadRef = useRef(load);
  loadRef.current = load;
  const unsavedRef = useRef(unsaved);
  unsavedRef.current = unsaved;
  // G3 (Muster der Glocke): Im verborgenen Tab ruht der Takt — das Jahr holte sonst je Minute bis 5 000 Einträge; beim
  // Sichtbarwerden lädt der Kalender einmal und der Takt läuft wieder.
  useEffect(() => {
    const tick = () => { if (!unsavedRef.current) void loadRef.current(); };
    const visible = () => document.visibilityState !== "hidden";
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (timer === null) timer = setInterval(tick, TICK_MS); };
    const stop = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
    const onVisibility = () => { if (visible()) { tick(); start(); } else stop(); };
    if (visible()) start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", tick);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", tick);
    };
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setMidnight((n) => n + 1), msBisMitternachtBerlin(new Date()) + 1000);
    return () => clearTimeout(t);
  }, [heute]);
  // G4: Erst die Antwort für DIESES Fenster sagt „Keine Termine.“; bis dahin zeichnet die Sicht den alten Stand ohne Aussage.
  const loaded = data !== null && data.from === from && data.to === to;
  // H11: `aria-busy` heißt „Laden läuft“ — scheiterte die Abfrage für dieses Fenster, meldet Hilfstechnik nicht dauerhaft
  // „beschäftigt“ (der Fehlerhinweis steht da).
  const busy = !loaded && failed !== `${from}|${to}`;

  // ── Einträge ───────────────────────────────────────────────────────────────────────────────────────────────
  // G8: nur die angefragten Quellen — ein abgeschalteter Chip wirkt sofort; bei „Tickets“ aus stehen bis zur Antwort nicht
  // die alten Tickets UND die nach D26 zurückgetretenen Fremdeinträge doppelt da.
  const entries = useMemo(() => visibleEntries(requestedItems(data?.items ?? [], requestKey.split(",")).map(entryFromDueItem),
    ticketsOn), [data, requestKey, ticketsOn]);
  const byDay = useMemo(() => entriesByDay(entries, from, to), [entries, from, to]);
  const overdueEntries = useMemo(() => (overdue
    ? entries.filter((e) => e.date < from && e.state === "OVERDUE").sort((a, b) => compareDueItems(a.item, b.item)) : []),
  [overdue, entries, from]);

  // ── Navigation ─────────────────────────────────────────────────────────────────────────────────────────────
  const go = (sicht: CalendarView, datum: string, replace: boolean) => p.onNavigiere({ sicht, datum, ticketId: null }, replace);
  // G1: Ein Sprung aus dem Rollfeld in eine andere Sicht (Tag im Jahr, „mehr“, Monatsname) hängt das fokussierte Element
  // aus; nach dem Zeichnen der neuen Sicht geht der Fokus an ihren Tabstopp bzw. den Kopf des Tages.
  const focusAfterJump = useRef(false);
  const jump = (sicht: CalendarView, datum: string) => { focusAfterJump.current = true; go(sicht, datum, false); };
  const gridRef = useRef<HTMLDivElement | null>(null);
  const keys = useCalendarKeys({ view, anchor, heute, from, to, gridRef,
    onLeave: (day) => go(view, day, true),
    onActivate: (day) => { if (view === "YEAR") jump("DAY", day); else keys.choose(day); } });
  const chooseView = (v: CalendarView) => {
    rememberCalendarView(userId, v);
    go(v, keys.focusDay, false);
  };
  const openTicket = (ticketId: string) => p.onNavigiere({ sicht: view, datum: anchor, ticketId }, false);
  const lastOpened = useRef<string | null>(null);
  const open = (e: CalendarEntry) => {
    lastOpened.current = e.key;
    const t = e.target;
    if (t.kind === "TICKET") openTicket(t.ticketId);
    else if (t.kind === "RECORD") oeffneOderSpringe(panels, terminBezug(t, e.item), { onGeaendert: reload });
    else setMilestone({ id: t.milestoneId, companyId: e.item.companyId, aside: false });
  };

  // F121-Muster: Schließt Dialog oder Panel und steht der Fokus nirgends, geht er an den zuletzt geöffneten Eintrag, beim
  // Ticket hilfsweise an einen Eintrag dieses Tickets (Direktaufruf), sonst an den Tabstopp des Rasters, sonst den Bereich.
  const section = useRef<HTMLElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const focusIsLost = () => document.activeElement === null || document.activeElement === document.body;
  const restoreFocus = useCallback((ticketId: string | null) => {
    if (!focusIsLost()) return;
    const all = [...(scroller.current?.querySelectorAll<HTMLElement>("button.kal__entry") ?? [])];
    const target = all.find((b) => b.dataset.key === lastOpened.current)
      ?? (ticketId === null ? undefined : all.find((b) => b.dataset.ticket === ticketId))
      ?? scroller.current?.querySelector<HTMLElement>('[role="gridcell"][tabindex="0"]') ?? section.current;
    target?.focus({ preventScroll: true });
  }, []);
  const openTicketBefore = useRef(p.route.ticketId);
  useEffect(() => {
    const before = openTicketBefore.current;
    openTicketBefore.current = p.route.ticketId;
    if (before !== null && p.route.ticketId === null) restoreFocus(before);
  }, [p.route.ticketId, restoreFocus]);
  const creatingBefore = useRef(creating);
  useEffect(() => {
    const before = creatingBefore.current;
    creatingBefore.current = creating;
    if (before !== null && creating === null) restoreFocus(null);
  }, [creating, restoreFocus]);
  const milestoneBefore = useRef(milestone);
  useEffect(() => {
    const before = milestoneBefore.current;
    milestoneBefore.current = milestone;
    if (before !== null && milestone === null) restoreFocus(null);
  }, [milestone, restoreFocus]);
  useEffect(() => {
    if (!focusAfterJump.current) return;
    focusAfterJump.current = false;
    if (focusIsLost()) scroller.current?.querySelector<HTMLElement>('[role="gridcell"][tabindex="0"], [data-jump-target]')?.focus();
  }, [view, anchor]);

  // ── Leiste ─────────────────────────────────────────────────────────────────────────────────────────────────
  const chooseTickets = (v: "MINE" | "ALL") => { setTickets(v); rememberCalendarTickets(userId, v); };
  const chooseSources = (s: DueSource[]) => { setSources(s); rememberDueSources(userId, s); };
  const backPopup = useSofortPopup<HTMLElement>(T.back);
  const forwardPopup = useSofortPopup<HTMLElement>(T.forward);
  const newMilestone = () =>
    setMilestone({ id: null, companyId: company === ALL_COMPANIES ? p.arbeitsunternehmenId : company, aside: false });
  const extraTabs: TicketDialogProps["zusatzReiter"] = [...(p.zusatzReiter ?? []),
    verknuepfungenReiter(p.client, (d, m) => setMilestone({ id: m.id, companyId: d.companyId, aside: true }))];
  /** Das Meilenstein-Panel schließt — nach seiner eigenen Frage, auch bevor es ein Ticket öffnet (K10). */
  const milestoneClosed = (changed: boolean) => {
    if (milestone === null) return;
    // Das Panel hat selbst gefragt: ohne zweite Frage — aber nur, wenn der Ticketdialog darunter nichts Ungespeichertes hat
    // (der Wächter liest synchron; sonst schützt er den Dialog weiter, Review M1). Kein `release`: der setzte auch den Merker
    // des Dialogs zurück.
    if (!dialogUnsaved) released.current = true;
    setPanelDirty(false);
    setMilestone(null);
    if (!changed) return;
    if (milestone.aside) setDialogReload((n) => n + 1);
    reload();
  };
  // F14: Eltern- und Kind-Ticket des Dialogs der Adresse öffnen seitlich im Gastgeber; der Dialog selbst liegt darunter.
  const panels = useObjektPanel();
  const openBeside = (ticketId: string) => {
    if (p.route.ticketId === null) return;
    panels.oeffne({ art: "ticket", id: ticketId }, { seitlich: true, darunter: { art: "ticket", id: p.route.ticketId },
      onGeaendert: () => { setDialogReload((n) => n + 1); reload(); } });
  };

  return (
    // tabIndex -1: Fokus-Ersatz nach dem Schließen von Dialog oder Panel (F129), wie die Ansichten der Tickets.
    <section className="kal__area" aria-label={T.area} ref={section} tabIndex={-1} data-view={view}>
      <Werkzeugleiste className="kal__toolbar" aria-label={T.toolbarName}
        aktiv={(company === ALL_COMPANIES ? 0 : 1) + (tickets === "MINE" ? 0 : 1)}
        onZurueckgesetzt={() => { setCompany(ALL_COMPANIES); chooseTickets("MINE"); }}
        filter={(
          <>
            <Werkzeuggruppe beschriftung={T.company} aktiv={company !== ALL_COMPANIES}>
              <Optionen aria-label={T.company} darstellung="segmente" richtung="waagerecht" wert={company}
                onGeaendert={(d) => setCompany(d.wert ?? ALL_COMPANIES)}>
                <option value={ALL_COMPANIES}>{T.allCompanies}</option>
                {p.companies.map((c) => <option key={c.id} value={c.id}>{c.shortCode}</option>)}
              </Optionen>
            </Werkzeuggruppe>
            <Werkzeuggruppe beschriftung={T.tickets} aktiv={tickets !== "MINE"}>
              <Optionen aria-label={T.tickets} darstellung="segmente" richtung="waagerecht" wert={tickets}
                onGeaendert={(d) => chooseTickets(d.wert === "ALL" ? "ALL" : "MINE")}>
                <option value="MINE">{T.mine}</option>
                <option value="ALL">{T.all}</option>
              </Optionen>
            </Werkzeuggruppe>
          </>
        )}
        // Ruling „Leiste kippt nie“: „+ Meilenstein“ hat seinen festen Platz und ist nur abgeschaltet, solange die Quelle
        // „Meilensteine“ aus ist (die Zeitachse verbirgt ihn dort mit Platz); me-knopf verwirft den Klick dann selbst.
        haupt={(
          <Knopf art="primaer" symbol={SYMBOL_PLUS} deaktiviert={!milestonesOn} onClick={newMilestone}>
            {T.newMilestone}
          </Knopf>
        )} />
      <div className="kal__controls">
        <DueSourceChips sources={chipSources("CALENDAR", p.ich.supplierView)} value={sources} onChange={chooseSources} />
        <div className="kal__period-group" role="group" aria-label={T.periodGroup}>
          <Optionen aria-label={T.viewChoice} darstellung="segmente" richtung="waagerecht" wert={view}
            onGeaendert={(d) => { const v = CALENDAR_VIEWS.find((x) => x === d.wert); if (v !== undefined) chooseView(v); }}>
            {CALENDAR_VIEWS.map((v) => <option key={v} value={v}>{T.views[v]}</option>)}
          </Optionen>
          {/* ◂ Heute ▸ bleiben zusammen: bricht die Gruppe um, dann vor oder nach diesem Block (Sichtprobe 31.10.26). */}
          <span className="kal__nav">
            <KiKnopf kompakt name={T.back} knopfRef={backPopup} onClick={() => go(view, shiftAnchor(view, anchor, -1), true)}>
              <Icon name="chevron-left" size={12} />
            </KiKnopf>
            <KiKnopf kompakt onClick={() => go(view, heute, true)}>{T.today}</KiKnopf>
            <KiKnopf kompakt name={T.forward} knopfRef={forwardPopup} onClick={() => go(view, shiftAnchor(view, anchor, 1), true)}>
              <Icon name="chevron-right" size={12} />
            </KiKnopf>
          </span>
          <span className="kal__period" aria-live="polite">{periodText(view, anchor)}</span>
        </div>
        {/* G1 (Hausregel 546): die Zustandszeichen erklärt, oben rechts; am Telefon entfällt sie. */}
        {!phone && <CalendarLegend />}
      </div>
      {failed !== null && <Hinweis art="fehler">{T.loadError}</Hinweis>}
      {data?.truncated === true && <Hinweis art="warnung">{T.limit}</Hinweis>}
      <div className="kal__rollfeld" ref={scroller} aria-busy={busy}>
        <CalendarViews view={view} anchor={anchor} heute={heute} from={from} byDay={byDay} overdue={overdueEntries}
          kalender={data?.kalender ?? []} showCompany={company === ALL_COMPANIES} phone={phone} loaded={loaded}
          keys={keys} gridRef={gridRef} onOpen={open}
          onCreateTicket={(item, fromKey) => { lastOpened.current = fromKey; setCreating(item); }}
          onOpenTicket={(id, fromKey) => { lastOpened.current = fromKey; openTicket(id); }}
          onDay={(day) => jump("DAY", day)} onMonth={(day) => jump("MONTH", day)} />
      </div>
      {creating !== null && (
        <TicketAnlegen key={creating.key} client={p.client} companies={p.companies} ich={p.ich} dueOrigin={{ item: creating }}
          vorgabeCompanyId={creating.companyId} arbeitsunternehmenId={p.arbeitsunternehmenId} onUngespeichert={setCreatingDirty}
          onAbbrechen={() => { release(); setCreating(null); }}
          onVorhanden={(id) => { release(); setCreating(null); setTakenTicket(id); reload(); openTicket(id); }}
          onAngelegt={(id) => { release(); setCreating(null); reload(); openTicket(id); }} />
      )}
      {p.route.ticketId !== null && (
        <TicketDialog key={p.route.ticketId} client={p.client} ticketId={p.route.ticketId} companies={p.companies}
          ich={{ id: userId, istAdmin: p.ich.istAdmin, supplierView: p.ich.supplierView }}
          pfad={T.area} zusatzReiter={extraTabs} onUngespeichert={dialogReports} onGeaendert={reload}
          reloadToken={dialogReload}
          {...(takenTicket === p.route.ticketId ? { notice: DUE_ITEM_TEXT.originTakenOpened } : {})}
          onOpenTicket={openBeside}
          onSchliessen={(changed) => {
            release();
            if (changed) reload();
            p.onNavigiere({ sicht: view, datum: anchor, ticketId: null }, false);
          }} />
      )}
      {milestone !== null && (
        <MilestonePanel key={milestone.id ?? "neu"} client={p.client} companies={p.companies} milestoneId={milestone.id}
          defaultCompanyId={milestone.companyId} {...(milestone.aside ? { unter: "seitlich" as const } : {})}
          onUngespeichert={setPanelDirty} onOpenTicket={(id, changed) => { milestoneClosed(changed); openTicket(id); }}
          onClose={milestoneClosed} />
      )}
    </section>
  );
}
